/**
 * Gift Wrap Cart — The India Craft House
 * Works on both the side cart drawer AND the /cart page.
 *
 * Sections are identified by [data-gift-variant-id] so the same
 * code handles both instances without coupling to specific IDs.
 */

(function () {
  'use strict';

  // ─── Shopify Cart API helpers ─────────────────────────────────────────────

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error('Cart request failed: ' + r.status);
      return r.json();
    });
  }

  function addGiftWrap(variantId, message) {
    return postJSON('/cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: message ? { 'Gift Message': message } : {} }],
    });
  }

  function removeGiftWrap(variantId) {
    return postJSON('/cart/update.js', { updates: { [variantId]: 0 } });
  }

  async function updateGiftMessage(variantId, message) {
    const cart = await fetch('/cart.js').then((r) => r.json());
    const giftItem = cart.items.find((i) => String(i.variant_id) === String(variantId));
    if (!giftItem) return;
    return postJSON('/cart/change.js', {
      line: giftItem.key,
      properties: { 'Gift Message': message },
    });
  }

  // ─── Theme cart refresh ───────────────────────────────────────────────────
  // Side cart:  call the theme's own updateSidebarCart if available.
  // Cart page:  reload the page so totals/line-items update correctly.

  function refreshCart(isCartPage) {
    if (isCartPage) {
      window.location.reload();
      return;
    }
    // Side cart — use theme helper if present
    if (window.sharedFunctions?.updateSidebarCart && window.Shopify?.getCart) {
      Shopify.getCart((cart) => window.sharedFunctions.updateSidebarCart(cart));
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      // Fallback: update visible cart count bubbles
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
          });
        });
    }
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────

  function showLoading(section) {
    section.querySelector('.gift-wrap-loading')?.style.setProperty('display', 'flex');
    section.querySelector('.gift-wrap-message-wrap')?.style.setProperty('display', 'none');
  }

  function hideLoading(section) {
    section.querySelector('.gift-wrap-loading')?.style.setProperty('display', 'none');
  }

  function showMessageBox(section) {
    const box = section.querySelector('.gift-wrap-message-wrap');
    if (!box) return;
    box.style.display = 'block';
    box.classList.remove('gift-wrap-slide-out');
    box.classList.add('gift-wrap-slide-in');
  }

  function hideMessageBox(section) {
    const box = section.querySelector('.gift-wrap-message-wrap');
    if (!box) return;
    box.classList.remove('gift-wrap-slide-in');
    box.classList.add('gift-wrap-slide-out');
    setTimeout(() => {
      box.style.display = 'none';
      box.classList.remove('gift-wrap-slide-out');
    }, 280);
  }

  function syncCharCount(section) {
    const textarea  = section.querySelector('.gift-wrap-message-textarea');
    const counter   = section.querySelector('.gift-wrap-char-count span');
    if (textarea && counter) {
      counter.textContent = textarea.value.length;
      textarea.addEventListener('input', () => { counter.textContent = textarea.value.length; });
    }
  }

  // ─── Per-section init ─────────────────────────────────────────────────────

  function initSection(section) {
    const checkbox  = section.querySelector('.gift-wrap-checkbox');
    const saveBtn   = section.querySelector('.gift-wrap-save-btn');
    const textarea  = section.querySelector('.gift-wrap-message-textarea');
    if (!checkbox) return;

    const variantId  = section.dataset.giftVariantId;
    const isCartPage = section.classList.contains('gift-wrap-section--page');

    syncCharCount(section);

    // ── Checkbox toggle ──
    checkbox.addEventListener('change', async () => {
      showLoading(section);
      try {
        if (checkbox.checked) {
          await addGiftWrap(variantId, '');
          section.dataset.giftInCart = 'true';
          if (isCartPage) {
            // On the cart page a reload is cleanest; message box shows after reload
            refreshCart(true);
            return;
          }
          hideLoading(section);
          showMessageBox(section);
        } else {
          await removeGiftWrap(variantId);
          section.dataset.giftInCart = 'false';
          if (isCartPage) {
            refreshCart(true);
            return;
          }
          hideLoading(section);
          hideMessageBox(section);
          if (textarea) textarea.value = '';
          syncCharCount(section);
        }
        refreshCart(false);
      } catch (err) {
        console.error('[Gift Wrap]', err);
        checkbox.checked = !checkbox.checked; // revert on failure
        hideLoading(section);
        if (checkbox.checked) showMessageBox(section);
        else hideMessageBox(section);
      }
    });

    // ── Save message ──
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const message = (textarea?.value || '').trim();
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        try {
          await updateGiftMessage(variantId, message);
          saveBtn.textContent = 'Saved ✓';
          setTimeout(() => {
            saveBtn.textContent = 'Save Message';
            saveBtn.disabled = false;
          }, 1800);
          if (!isCartPage) refreshCart(false);
        } catch (err) {
          console.error('[Gift Wrap] save message failed', err);
          saveBtn.textContent = 'Save Message';
          saveBtn.disabled = false;
        }
      });
    }
  }

  // ─── Bootstrap ────────────────────────────────────────────────────────────
  // MutationObserver handles side-cart re-renders.
  // Direct init handles the static /cart page.

  const SELECTOR = '.gift-wrap-section';

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((section) => {
      if (!section.dataset.giftWrapReady) {
        section.dataset.giftWrapReady = 'true';
        initSection(section);
      }
    });
  }

  function startObserver() {
    new MutationObserver(initAll).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initAll(); startObserver(); });
  } else {
    initAll();
    startObserver();
  }
})();